import type { AppState } from '@/store/app-state';
import type { CloudState } from '@/store/cloud-types';
import type { ProjectScope } from './project-scope';
import { extractCloudState } from './project-cloud-state-extraction';
import type { PendingGlobalCloudField } from './project-remote-update';
import {
    getPendingGlobalCloudFieldsForAutosave,
    shouldSaveScriptBlocksForAutosave,
    shouldSkipCloudAutosaveForMetaUpdate,
    shouldSkipCloudAutosaveForRemoteUpdate,
    shouldSkipQueuedCloudAutosaveForRemoteVersion,
} from './project-remote-update';
export {
    shouldApplyRemoteSnapshotForCloudAuthority,
    shouldSkipCloudAutosaveForMetaUpdate,
    shouldSkipCloudAutosaveForRemoteUpdate,
    shouldSkipQueuedCloudAutosaveForRemoteVersion,
} from './project-remote-update';

type CloudConnectionState = Partial<AppState['cloud']>;
type AppStateSnapshot = CloudState & { cloud: CloudConnectionState };
type ShowStructureComparisonState = Pick<CloudState, 'showStructure'>;
type ScriptBlocks = NonNullable<CloudState['showStructure']['scriptBlocks']>;

export type CloudAutosavePlan = {
    shouldQueue: boolean;
    pendingGlobalFields: PendingGlobalCloudField[];
    shouldSaveScriptBlocks: boolean;
};

export type CloudAutosavePlanInput = {
    changedKeys: readonly PropertyKey[];
    currentBlocks: CloudState['showStructure']['scriptBlocks'];
    previousBlocks: CloudState['showStructure']['scriptBlocks'];
    hasInitialLoad: boolean;
    isApplyingRemoteSnapshot: boolean;
    isUpdatingCloudMetadata: boolean;
    currentState?: ShowStructureComparisonState;
    previousState?: ShowStructureComparisonState;
};

export function createCloudAutosavePlan(input: CloudAutosavePlanInput): CloudAutosavePlan {
    const hasCloudChanges = input.changedKeys.length > 0;
    const shouldSaveScriptBlocks = shouldSaveScriptBlocksForAutosave(
        input.currentBlocks,
        input.previousBlocks,
    );

    if (shouldSkipCloudAutosaveForRemoteUpdate(input.isApplyingRemoteSnapshot)) {
        return { shouldQueue: false, pendingGlobalFields: [], shouldSaveScriptBlocks: false };
    }

    if (shouldSkipCloudAutosaveForMetaUpdate(input.isUpdatingCloudMetadata, hasCloudChanges)) {
        return { shouldQueue: false, pendingGlobalFields: [], shouldSaveScriptBlocks };
    }

    if (!hasCloudChanges || !input.hasInitialLoad) {
        return { shouldQueue: false, pendingGlobalFields: [], shouldSaveScriptBlocks };
    }

    return {
        shouldQueue: true,
        pendingGlobalFields: getPendingGlobalCloudFieldsForAutosave(
            input.changedKeys,
            input.currentState,
            input.previousState,
        ),
        shouldSaveScriptBlocks,
    };
}

export type RunCloudAutosaveInput = {
    currentCloud: CloudConnectionState;
    loadedResources: ReadonlySet<string>;
    pendingScriptBlocksSave: boolean;
    getActorId: () => string | undefined;
    getState: () => AppStateSnapshot;
    saveCloudProject: (scope: ProjectScope, state: Partial<CloudState>, actorId: string) => Promise<void>;
    saveScriptBlocks: (scope: ProjectScope, blocks: ScriptBlocks) => Promise<unknown>;
    markSynced: () => void;
    warn: (message: string, detail?: unknown) => void;
};

type CloudAutosaveTimer = ReturnType<typeof setTimeout>;

export type CloudAutosaveQueueInput<TCloud extends CloudConnectionState> = {
    timeoutRef: { current: CloudAutosaveTimer | null };
    delayMs?: number;
    getRemoteVersion: () => number;
    hasPendingLocalAutosave: () => boolean;
    isSaving: () => boolean;
    setSaving: (value: boolean) => void;
    runAutosave: (currentCloud: TCloud) => Promise<unknown>;
    warn: (message: string, detail?: unknown) => void;
    setTimer?: typeof setTimeout;
    clearTimer?: typeof clearTimeout;
};

export type CloudAutosaveQueue<TCloud extends CloudConnectionState> = {
    queue: (currentCloud: TCloud, versionAtSchedule: number) => void;
    clear: () => void;
};

export function createCloudAutosaveQueue<TCloud extends CloudConnectionState>(
    input: CloudAutosaveQueueInput<TCloud>,
): CloudAutosaveQueue<TCloud> {
    const delayMs = input.delayMs ?? 3000;
    const setTimer = input.setTimer ?? setTimeout;
    const clearTimer = input.clearTimer ?? clearTimeout;

    const clear = () => {
        if (!input.timeoutRef.current) return;
        clearTimer(input.timeoutRef.current);
        input.timeoutRef.current = null;
    };

    return {
        queue: (currentCloud, versionAtSchedule) => {
            clear();

            input.timeoutRef.current = setTimer(() => {
                input.timeoutRef.current = null;

                if (shouldSkipQueuedCloudAutosaveForRemoteVersion({
                    currentRemoteVersion: input.getRemoteVersion(),
                    versionAtSchedule,
                    hasPendingLocalAutosave: input.hasPendingLocalAutosave(),
                })) {
                    return;
                }
                if (input.isSaving()) return;

                input.setSaving(true);
                void input.runAutosave(currentCloud)
                    .catch((error: unknown) => {
                        input.warn('[useSync] Auto-save failed, will retry on next change', error);
                    })
                    .finally(() => {
                        input.setSaving(false);
                    });
            }, delayMs);
        },
        clear,
    };
}

export async function runCloudAutosave(input: RunCloudAutosaveInput): Promise<boolean> {
    const latestState = input.getState();
    const actorId = input.getActorId();
    if (!actorId) return false;

    const saveOrgId = input.currentCloud.orgId;
    const saveProjectId = input.currentCloud.projectId;
    if (!saveOrgId || !saveProjectId) {
        throw new Error('Cannot save cloud project without organization scope');
    }

    if (
        latestState.cloud.orgId !== saveOrgId
        || latestState.cloud.projectId !== saveProjectId
    ) {
        input.warn('[useSync] Project ID changed during debounce. Aborting save to prevent overwrite.', {
            initial: `${saveOrgId}/${saveProjectId}`,
            current: `${latestState.cloud.orgId}/${latestState.cloud.projectId}`,
        });
        return false;
    }

    const saveScope = { orgId: saveOrgId, projectId: saveProjectId };
    const savePromises: Promise<unknown>[] = [
        input.saveCloudProject(saveScope, extractCloudState(latestState, input.loadedResources, {
            includePersonnel: latestState.cloud.hasLoadedPersonnel === true,
        }), actorId),
    ];

    if (input.pendingScriptBlocksSave && latestState.showStructure.scriptBlocks?.length) {
        savePromises.push(input.saveScriptBlocks(saveScope, latestState.showStructure.scriptBlocks));
    }

    await Promise.all(savePromises);
    input.markSynced();
    return true;
}
